import { usarSessao, type TokensSessao } from '../estado/sessao';
import { ErroApi } from './erros';
import { requisicaoPublica } from './http';

/**
 * Contrato de autenticação — `apps/api/src/modules/auth/auth.controller.ts`.
 *
 * O nome dos campos é o do backend (`emailAddress`, `password`), de propósito:
 * renomear na fronteira cria uma tradução a mais para manter em dia, e é
 * exatamente onde nasce o tipo de divergência que a auditoria encontrou entre
 * Swagger e comportamento real (dívida técnica §6).
 */
export interface CredenciaisLogin {
  readonly emailAddress: string;
  readonly password: string;
}

/** `POST /api/auth/login` — 5 tentativas por minuto por IP. */
export async function entrar(credenciais: CredenciaisLogin): Promise<TokensSessao> {
  const tokens = await requisicaoPublica<TokensSessao>('/auth/login', {
    metodo: 'POST',
    corpo: credenciais,
  });

  if (typeof tokens?.accessToken !== 'string' || typeof tokens?.refreshToken !== 'string') {
    throw new ErroApi(
      502,
      'O servidor respondeu ao login em formato inesperado.',
      [],
      tokens as unknown,
    );
  }

  usarSessao.getState().iniciar(tokens);
  return tokens;
}

/**
 * `POST /api/auth/logout` — encerra a sessão apresentada.
 *
 * A sessão local é limpa **sempre**, mesmo se a chamada falhar: se o usuário
 * clicou em sair, ele saiu. Manter os tokens na máquina porque o servidor não
 * respondeu deixaria a próxima pessoa no mesmo computador dentro da conta.
 * A revogação no servidor, se falhou aqui, ainda acontece pela expiração do
 * refresh (30 dias) ou por "sair de todas as sessões".
 */
export async function sair(): Promise<void> {
  const { refreshToken, encerrar } = usarSessao.getState();

  try {
    if (refreshToken !== null) {
      await requisicaoPublica<void>('/auth/logout', {
        metodo: 'POST',
        corpo: { refreshToken },
      });
    }
  } finally {
    encerrar();
  }
}
